import React, { useState, useEffect } from "react";
import LoadingSpinner from "../LoadingSpinner.jsx";

const DataTable = ({
  columns,
  fetchDataFn,
  defaultSortField = "createdAt",
  defaultSortDirection = "desc",
  useCheckbox = false,
  checkboxKey = "id",
  onSelectedCheckboxChange,
  useSelectRow = false,
  selectRowKey = "id",
  onSelectedRowChange,
  filters = {},
  pageSize = 10,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  // 데이터 로드 함수
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        size: pageSize,
        sortField,
        direction: sortDirection,
        ...filters,
      };

      const response = await fetchDataFn(params);

      if (response.success && response.data) {
        setData(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      } else {
        throw new Error(
          response.message || "데이터를 불러오는데 실패했습니다."
        );
      }
    } catch (err) {
      // console.error("데이터 로드 실패:", err);
      setError(err.message || "데이터를 불러오는데 실패했습니다.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // 초기 로드 및 의존성 변경 시 리로드
  useEffect(() => {
    loadData();
  }, [currentPage, sortField, sortDirection, JSON.stringify(filters)]);

  // 페이지 변경
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 정렬 변경
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(0);
  };

  // 체크박스 변경
  const handleCheckboxChange = (value, checked) => {
    let newSelected;
    if (checked) {
      newSelected = [...selectedCheckboxes, value];
    } else {
      newSelected = selectedCheckboxes.filter((item) => item !== value);
    }
    setSelectedCheckboxes(newSelected);
    if (onSelectedCheckboxChange) {
      onSelectedCheckboxChange(newSelected);
    }
  };

  // 전체 선택/해제
  const handleSelectAll = (checked) => {
    if (checked) {
      const allValues = data.map((item) => item[checkboxKey]);
      setSelectedCheckboxes(allValues);
      if (onSelectedCheckboxChange) {
        onSelectedCheckboxChange(allValues);
      }
    } else {
      setSelectedCheckboxes([]);
      if (onSelectedCheckboxChange) {
        onSelectedCheckboxChange([]);
      }
    }
  };

  // 행 선택
  const handleRowClick = (item) => {
    if (useSelectRow) {
      const key = item[selectRowKey];
      setSelectedRow(key);
      if (onSelectedRowChange) {
        onSelectedRowChange(key);
      }
    }
  };

  // 페이지네이션 렌더링
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      0,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    // 이전 버튼
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 0}
        className="px-3 py-1 mx-1 text-sm bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
      >
        이전
      </button>
    );

    // 페이지 번호들
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 mx-1 text-sm rounded ${
            currentPage === i
              ? "bg-blue-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          {i + 1}
        </button>
      );
    }

    // 다음 버튼
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage >= totalPages - 1}
        className="px-3 py-1 mx-1 text-sm bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
      >
        다음
      </button>
    );

    return pages;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500 text-center">
          <p className="text-lg font-medium">오류가 발생했습니다</p>
          <p className="text-sm mt-2">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* 테이블 */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {useCheckbox && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                  <input
                    type="checkbox"
                    checked={
                      data.length > 0 &&
                      selectedCheckboxes.length === data.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.accessor}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b ${
                    column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                  }`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.accessor)}
                >
                  <div className="flex items-center">
                    {column.Header}
                    {column.sortable && sortField === column.accessor && (
                      <span className="ml-1">
                        {sortDirection === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (useCheckbox ? 1 : 0)}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr
                  key={item[selectRowKey] || index}
                  className={`hover:bg-gray-50 ${
                    useSelectRow && selectedRow === item[selectRowKey]
                      ? "bg-blue-50"
                      : ""
                  } ${useSelectRow ? "cursor-pointer" : ""}`}
                  onClick={() => handleRowClick(item)}
                >
                  {useCheckbox && (
                    <td className="px-4 py-3 whitespace-nowrap border-b">
                      <input
                        type="checkbox"
                        checked={selectedCheckboxes.includes(item[checkboxKey])}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleCheckboxChange(
                            item[checkboxKey],
                            e.target.checked
                          );
                        }}
                        className="rounded"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.accessor}
                      className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 border-b"
                      style={{ width: column.width }}
                    >
                      {column.Cell ? (
                        <column.Cell row={{ original: item }} />
                      ) : (
                        item[column.accessor]
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-700">
            총 {totalElements}개 중 {currentPage * pageSize + 1}-
            {Math.min((currentPage + 1) * pageSize, totalElements)}개 표시
          </div>
          <div className="flex">{renderPagination()}</div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
